// src/careComponents/pages/providerAgreementPage.tsx
import React, { useMemo, useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApolloClient, useQuery, useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import jwtDecode from "jwt-decode";
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Divider,
} from "@mui/material";

import {
  GET_CARE_PRICING_CATALOG,
  GET_CONCIERGE_BROADCAST_GROUPS,
  GET_CONCIERGE_LEADS,
  GET_LATEST_PROVIDER_AGREEMENT_TEMPLATE,
  GET_PROVIDER_AGREEMENT_STATUS,
  GET_REFERRAL_LEADS,
} from "../../../../platform/graphql/queries/care";
import { GET_TOKEN as GET_PROVIDER_TOKEN } from "../../../../platform/graphql/queries";
import {
  ACCEPT_PROVIDER_AGREEMENT,
  ACCEPT_PROVIDER_AGREEMENT_FOR_LEAD,
  RELEASE_BUSINESS_CLAIM,
} from "../../../../platform/graphql/mutations/care";
import { PROVIDER_RESPOND_INVITE } from "../../careReferral/components/broadcastInvitesPanel";
import useBusinessFromStore from "../../../../platform/careComponents/hooks/useBusinessFromStore";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { SEND_EMAIL_MUTATION } from "../../../../platform/graphql/mutations";
import { UPDATE_DEFAULT_BUSINESS } from "../../../../platform/graphql/mutations/generic";
import { setSelectedBusiness } from "../../../../platform/redux/actions/businessActions";
import { getToken, setToken } from "../../../../platform/util/authUtils";
import { parseExtra } from "./conciergeBoardShared";

const AGREEMENT_CONTEXT_KEY = "pendingAgreementBusinessContext";

/** USD without cents (e.g., $2,500) */
const usd0 = (n: number) =>
  Number(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const normCode = (s: string) =>
  (s || "")
    .toString()
    .trim()
    .replace(/[\s-]+/g, "_")
    .toUpperCase();

const PRICE_RE = /\{\{\s*(?:PRICE|price)\s*:\s*([^}]+)\s*\}\}/g;

/* ============================================================
   KEY FIX: SMART FORMATTER
   If template body arrives as one giant line, add newlines so
   the "Formatted" viewer + email renderer can detect structure.
   ============================================================ */

/**
 * If the agreement content is mostly one line, this function inserts
 * line breaks before:
 * - Section headers: "1) ", "2) ", etc.
 * - Bullets: " - "
 * - Common label lines: "...:" like "Per-bed minimums and rush adder:"
 *
 * Safe: It does not use HTML, it only returns plain text with \n.
 */
function smartFormatAgreementText(input: string) {
  if (!input) return "";

  // normalize newlines
  let s = input.replace(/\r\n/g, "\n");

  // If there are already newlines, do minimal cleanup only
  const hasNewlines = s.includes("\n");

  // Normalize weird spacing
  s = s.replace(/[ \t]+/g, " ").trim();

  // If it was already multiline, return with normalized spaces around newlines
  if (hasNewlines) {
    s = s
      .split("\n")
      .map((line) => line.trimEnd())
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    return s;
  }

  // 1) Put a blank line before numbered sections (except if at start)
  // Example: "... effective 06/02/2026. 1) Services ..."
  s = s.replace(/(\.\s+)(?=\d+\)\s+)/g, ".\n\n");

  // Also handle if sections appear without a period separator
  s = s.replace(/(\s+)(?=\d+\)\s+)/g, "\n\n");

  // 2) Convert inline " - " bullets into real bullet lines
  // Example: "1) Services - Listing ... - No guarantee ..."
  s = s.replace(/\s-\s+/g, "\n- ");

  // 3) Put labels (lines ending with ":") on their own line.
  // Works for: "Per-bed minimums and rush adder:", "Per-bed fee formula:", etc.
  // We insert a blank line before them if they appear mid-line.
  s = s.replace(/(\s+)([A-Za-z][A-Za-z0-9 /()&,]+:)/g, "\n\n$2");

  // 4) Add blank line before each section header again (after bullet conversion)
  s = s.replace(/\n(?=\d+\)\s+)/g, "\n\n");

  // 5) Cleanup: reduce too many blank lines
  s = s.replace(/\n{3,}/g, "\n\n").trim();

  return s;
}

/* ============================================================
   EMAIL HELPERS (TABLE-BASED + INLINE STYLES)
   ============================================================ */

type AgreementBlock =
  | { type: "header"; text: string }
  | { type: "label"; text: string }
  | { type: "para"; text: string }
  | { type: "bullets"; items: string[] }
  | { type: "spacer" };

const normalizeNewlines = (s: string) => (s ?? "").replace(/\r\n/g, "\n");

const escapeHtml = (s: string) =>
  (s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

function collectImplicitListItems(lines: string[], startIndex: number) {
  const items: string[] = [];
  let i = startIndex;

  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    if (!trimmed) {
      i++;
      continue;
    }

    if (/^\s*\d+\)\s+/.test(raw) || /^\s*\d+\.\s+/.test(raw)) break;
    if (/:$/.test(trimmed)) break;

    if (/^\s*-\s+/.test(raw)) {
      items.push(raw.replace(/^\s*-\s+/, "").trim());
    } else {
      items.push(trimmed);
    }
    i++;
  }

  return { items, nextIndex: i };
}

function parseAgreementBlocks(body: string): AgreementBlock[] {
  const lines = normalizeNewlines(body).split("\n");
  const blocks: AgreementBlock[] = [];

  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];

    if (!raw.trim()) {
      blocks.push({ type: "spacer" });
      i++;
      continue;
    }

    if (/^\s*\d+\)\s+/.test(raw) || /^\s*\d+\.\s+/.test(raw)) {
      blocks.push({ type: "header", text: raw.trim() });
      i++;
      continue;
    }

    if (/^\s*-\s+/.test(raw)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*-\s+/, "").trim());
        i++;
      }
      blocks.push({ type: "bullets", items });
      continue;
    }

    if (/:$/.test(raw.trim())) {
      const label = raw.trim();
      const { items, nextIndex } = collectImplicitListItems(lines, i + 1);
      blocks.push({ type: "label", text: label });
      if (items.length >= 2) {
        blocks.push({ type: "bullets", items });
        i = nextIndex;
        continue;
      }
      i++;
      continue;
    }

    blocks.push({ type: "para", text: raw.trimEnd() });
    i++;
  }

  return blocks;
}

function renderMathLiteralsEmail(text: string) {
  const escaped = escapeHtml(text);
  return escaped.replace(/\\\((.+?)\\\)/g, (_m, inner) => {
    const safeInner = escapeHtml(inner);
    return `<span style="font-family:Consolas,Menlo,Monaco,monospace;">\\(${safeInner}\\)</span>`;
  });
}

function blocksToEmailHtml(blocks: AgreementBlock[]) {
  const parts: string[] = [];

  for (const b of blocks) {
    if (b.type === "spacer") {
      parts.push(`<div style="height:10px; line-height:10px;">&nbsp;</div>`);
      continue;
    }

    if (b.type === "header") {
      parts.push(`
        <div style="margin:14px 0 6px 0; font-size:16px; font-weight:700; color:#111;">
          ${renderMathLiteralsEmail(b.text)}
        </div>
      `);
      continue;
    }

    if (b.type === "label") {
      parts.push(`
        <div style="margin:10px 0 4px 0; font-size:14px; font-weight:600; color:#111;">
          ${renderMathLiteralsEmail(b.text)}
        </div>
      `);
      continue;
    }

    if (b.type === "bullets") {
      const lis = b.items
        .map(
          (it) => `
          <li style="margin:0 0 6px 0;">
            <span style="font-size:14px; color:#111; line-height:20px;">
              ${renderMathLiteralsEmail(it)}
            </span>
          </li>
        `
        )
        .join("");

      parts.push(`
        <ul style="margin:6px 0 10px 18px; padding:0;">
          ${lis}
        </ul>
      `);
      continue;
    }

    const text = b.text?.trim() ?? "";
    if (!text) {
      parts.push(`<div style="height:10px; line-height:10px;">&nbsp;</div>`);
    } else {
      parts.push(`
        <div style="margin:6px 0; font-size:14px; color:#111; line-height:20px;">
          ${renderMathLiteralsEmail(text)}
        </div>
      `);
    }
  }

  return parts.join("\n");
}

function buildAgreementEmailHtml(opts: {
  title: string;
  acceptedOnText: string;
  agreementBody: string;
  businessName?: string;
  businessAddress?: string;
  platformName?: string;
}) {
  const blocks = parseAgreementBlocks(opts.agreementBody);
  const bodyHtml = blocksToEmailHtml(blocks);

  const title = escapeHtml(opts.title);
  const acceptedOnText = escapeHtml(opts.acceptedOnText);
  const businessName = escapeHtml(opts.businessName ?? "");
  const businessAddress = escapeHtml(opts.businessAddress ?? "");
  const platformName = escapeHtml(opts.platformName ?? "Plany365 Care");

  return `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${title}</title>
</head>
<body style="margin:0; padding:0; background:#f5f6f8;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#f5f6f8;">
    <tr>
      <td align="center" style="padding:18px 12px;">
        <table role="presentation" width="720" cellspacing="0" cellpadding="0" border="0"
               style="width:720px; max-width:720px; background:#ffffff; border:1px solid #e7e7ea; border-radius:10px;">
          <tr>
            <td style="padding:18px 18px 10px 18px; font-family:Segoe UI, Arial, sans-serif;">
              <div style="font-size:20px; font-weight:700; color:#111; margin:0 0 6px 0;">${title}</div>
              <div style="font-size:12px; color:#666; margin:0 0 12px 0;">Accepted on ${acceptedOnText}</div>
              <div style="height:1px; background:#eeeeef; line-height:1px;">&nbsp;</div>
            </td>
          </tr>

          <tr>
            <td style="padding:10px 18px 6px 18px; font-family:Segoe UI, Arial, sans-serif;">
              ${bodyHtml}
            </td>
          </tr>

          <tr>
            <td style="padding:12px 18px 18px 18px; font-family:Segoe UI, Arial, sans-serif;">
              <div style="height:1px; background:#eeeeef; line-height:1px; margin:12px 0;">&nbsp;</div>
              <div style="font-size:12px; color:#777; line-height:18px;">
                <div><strong>Business:</strong> ${businessName}</div>
                <div><strong>Address:</strong> ${businessAddress}</div>
                <div>Generated by ${platformName}</div>
              </div>
            </td>
          </tr>
        </table>

        <div style="font-family:Segoe UI, Arial, sans-serif; color:#999; font-size:11px; margin-top:10px;">
          This email contains a copy of the agreement accepted electronically.
        </div>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

function buildAgreementEmailText(opts: {
  title: string;
  acceptedOnText: string;
  agreementBody: string;
  businessName?: string;
  businessAddress?: string;
  platformName?: string;
}) {
  const platformName = opts.platformName ?? "Plany365 Care";
  return [
    opts.title,
    `Accepted on ${opts.acceptedOnText}`,
    "",
    opts.agreementBody,
    "",
    `Business: ${opts.businessName ?? ""}`,
    `Address: ${opts.businessAddress ?? ""}`,
    `Generated by ${platformName}`,
    "",
    "This email contains a copy of the agreement accepted electronically.",
  ].join("\n");
}

function normalizeRecipientList(values: Array<string | null | undefined>) {
  const seen = new Set<string>();
  const recipients: string[] = [];
  values.forEach((value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    recipients.push(normalized);
  });
  return recipients;
}

/* ============================================================
   UI VIEWER
   ============================================================ */

function AgreementPrettyViewer({ body }: { body: string }) {
  const renderMathLiterals = (text: string) => {
    const regex = /\\\((.+?)\\\)/g;
    const parts: Array<string | { math: string }> = [];
    let lastIndex = 0;
    let m: RegExpExecArray | null;

    while ((m = regex.exec(text))) {
      if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
      parts.push({ math: m[1] });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));

    return parts.map((p, i) =>
      typeof p === "string" ? (
        <React.Fragment key={i}>{p}</React.Fragment>
      ) : (
        <code
          key={i}
          style={{
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            padding: "0px 4px",
            borderRadius: 4,
            border: "1px solid rgba(0,0,0,0.12)",
            background: "rgba(0,0,0,0.04)",
            whiteSpace: "pre-wrap",
          }}
        >
          {"\\("}
          {` ${p.math} `}
          {"\\)"}
        </code>
      )
    );
  };

  const lines = useMemo(() => body.split(/\r?\n/), [body]);

  const blocks: Array<
    | { type: "header"; text: string }
    | { type: "label"; text: string }
    | { type: "para"; text: string }
    | { type: "bullets"; items: string[] }
  > = [];

  let i = 0;
  while (i < lines.length) {
    const raw = lines[i];

    if (!raw.trim()) {
      blocks.push({ type: "para", text: "" });
      i++;
      continue;
    }

    if (/^\s*\d+\)\s+/.test(raw) || /^\s*\d+\.\s+/.test(raw)) {
      blocks.push({ type: "header", text: raw.trim() });
      i++;
      continue;
    }

    if (/^\s*-\s+/.test(raw)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*-\s+/, ""));
        i++;
      }
      blocks.push({ type: "bullets", items });
      continue;
    }

    if (/:$/.test(raw.trim())) {
      const label = raw.trim();
      const { items, nextIndex } = collectImplicitListItems(lines, i + 1);
      blocks.push({ type: "label", text: label });
      if (items.length >= 2) {
        blocks.push({ type: "bullets", items });
        i = nextIndex;
        continue;
      }
      i++;
      continue;
    }

    blocks.push({ type: "para", text: raw });
    i++;
  }

  return (
    <Box sx={{ maxWidth: 860, mx: "auto" }}>
      {blocks.map((b, idx) => {
        switch (b.type) {
          case "header":
            return (
              <Typography
                key={idx}
                variant="subtitle1"
                sx={{ fontWeight: 700, mt: idx === 0 ? 0 : 1.75, mb: 0.75 }}
              >
                {renderMathLiterals(b.text)}
              </Typography>
            );
          case "label":
            return (
              <Typography key={idx} sx={{ fontWeight: 600, mt: 1.25, mb: 0.4 }}>
                {renderMathLiterals(b.text)}
              </Typography>
            );
          case "bullets":
            return (
              <Box key={idx} component="ul" sx={{ pl: 2.75, my: 0.25 }}>
                {b.items.map((it, j) => (
                  <li key={j} style={{ marginBottom: 4 }}>
                    <Typography component="span" sx={{ lineHeight: 1.65 }}>
                      {renderMathLiterals(it)}
                    </Typography>
                  </li>
                ))}
              </Box>
            );
          case "para":
          default:
            if (!b.text) return <Box key={idx} sx={{ height: 10 }} />;
            return (
              <Typography key={idx} sx={{ lineHeight: 1.7, mb: 0.65 }}>
                {renderMathLiterals(b.text)}
              </Typography>
            );
        }
      })}
    </Box>
  );
}

function useQueryParam(name: string) {
  const { search } = useLocation();
  return useMemo(() => {
    const raw = new URLSearchParams(search).get(name);
    if (raw == null) return null;
    const v = raw.trim();
    if (!v || v.toLowerCase() === "null" || v.toLowerCase() === "undefined")
      return null;
    return v;
  }, [search, name]);
}

export default function ProviderAgreementPage() {
  const apolloClient = useApolloClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const leadId = useQueryParam("lead");
  const inviteId = useQueryParam("invite");
  const businessIdParam = useQueryParam("businessId");
  const returnTo = useQueryParam("returnTo");

  const { providerUser, updateProviderUser } = useContext(UserContext) as {
    providerUser: { email?: string | null; userId?: number | null; defaultBusinessId?: number | null } | null;
    updateProviderUser: (next: Record<string, any>) => void;
  };
  const userEmail = providerUser?.email || "";

  const { business, businessId, ready, loading: storeLoading } =
    useBusinessFromStore();
  const businessBasic = business?.businessBasic;
  const targetBusinessId = useMemo(() => {
    const queryBizId = Number(businessIdParam || 0);
    if (Number.isFinite(queryBizId) && queryBizId > 0) return queryBizId;
    return Number(businessId || 0);
  }, [businessIdParam, businessId]);

  useEffect(() => {
    if (!targetBusinessId) return;
    dispatch(setSelectedBusiness(targetBusinessId));
  }, [targetBusinessId, dispatch]);

  const [checked, setChecked] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"formatted" | "plain">("formatted");
  const { data: leadsData } = useQuery(GET_REFERRAL_LEADS, {
    variables: {
      businessId: Number(targetBusinessId),
      limit: 100,
      offset: 0,
    },
    skip: !leadId || !targetBusinessId,
    fetchPolicy: "cache-and-network",
  });

  const agreementLead = useMemo(() => {
    if (!leadId) return null;
    const leads = Array.isArray(leadsData?.referralLeads) ? leadsData.referralLeads : [];
    return leads.find((lead: any) => String(lead?.id) === String(leadId)) || null;
  }, [leadId, leadsData?.referralLeads]);
  const agreementLeadExtra = useMemo(
    () => parseExtra(agreementLead?.extra),
    [agreementLead?.extra]
  );
  const agreementLeadSourceType = String(
    agreementLead?.leadSourceType ||
      agreementLead?.attributionSummary?.leadSourceType ||
      agreementLeadExtra?.leadSource?.type ||
      "PLATFORM_DIRECT"
  )
    .trim()
    .toUpperCase();
  const agreementSourceAgentName =
    String(
      agreementLead?.sourceAgentName ||
        agreementLead?.attributionSummary?.sourceAgentName ||
        agreementLeadExtra?.leadSource?.sourceAgentName ||
        ""
    ).trim() || null;

  const {
    data: tplData,
    loading: tplLoading,
    error: tplError,
  } = useQuery(GET_LATEST_PROVIDER_AGREEMENT_TEMPLATE, {
    skip: !targetBusinessId,
    fetchPolicy: "network-only",
  });

  const { data: pricingData } = useQuery(GET_CARE_PRICING_CATALOG, {
    fetchPolicy: "cache-first",
  });

  const priceByCode = useMemo(() => {
    const list: { code: string; amount?: number | null }[] =
      pricingData?.getCarePricingCatalog ?? [];
    const m = new Map<string, number>();
    for (const r of list) {
      const val = r.amount as number | undefined;
      if (val != null) m.set(normCode(r.code), Number(val));
    }
    return m;
  }, [pricingData]);

  const replacePricePlaceholders = useCallback(
    (text: string): string =>
      text.replace(PRICE_RE, (_match, rawCode) => {
        const amt = priceByCode.get(normCode(rawCode));
        return Number.isFinite(amt as number) ? usd0(amt as number) : _match;
      }),
    [priceByCode],
  );

  const merged = useMemo(() => {
    const tpl = tplData?.getLatestProviderAgreementTemplate;
    if (!tpl) return null;

    let body = tpl.bodyMarkdown ?? "";
    body = body
      .replaceAll("{{providerLegalName}}", businessBasic?.name ?? "")
      .replaceAll("{{businessName}}", businessBasic?.name ?? "")
      .replaceAll("{{businessAddress}}", businessBasic?.address ?? "")
      .replaceAll("{{platformName}}", "Plany365 Care")
      .replaceAll("{{effectiveDate}}", new Date().toLocaleDateString());

    body = replacePricePlaceholders(body);

    // IMPORTANT: Apply smart formatting so viewer + email can render nicely
    const formattedBody = smartFormatAgreementText(body);

    return {
      title: tpl.title ?? "Provider Service Agreement",
      version: tpl.version ?? 1,
      body: formattedBody,
    };
  }, [tplData, businessBasic?.name, businessBasic?.address, replacePricePlaceholders]);

  useEffect(() => {
    if (!merged?.body) return;
    const iter = merged.body.matchAll(PRICE_RE) as Iterable<RegExpMatchArray>;
    const remainingCodes = Array.from(iter, (mm) => normCode(mm[1] ?? ""));
    if (remainingCodes.length) {
      console.warn("Unresolved PRICE codes still in body:", remainingCodes);
    }
  }, [merged?.body]);

  const [acceptGlobal, { loading: savingGlobal }] = useMutation(
    ACCEPT_PROVIDER_AGREEMENT,
    {
      refetchQueries: (res) => {
        const ok = res?.data?.acceptProviderAgreement?.success === true;
        if (!ok || !targetBusinessId) return [];
        return [
          {
            query: GET_PROVIDER_AGREEMENT_STATUS,
            variables: { businessId: Number(targetBusinessId) },
          },
        ];
      },
      awaitRefetchQueries: true,
    }
  );

  const [acceptForLead, { loading: savingLead }] = useMutation(
    ACCEPT_PROVIDER_AGREEMENT_FOR_LEAD,
    {
      refetchQueries: (res) => {
        const ok = res?.data?.acceptProviderAgreementForLead?.success === true;
        if (!ok || !targetBusinessId) return [];
        return [
          {
            query: GET_PROVIDER_AGREEMENT_STATUS,
            variables: { businessId: Number(targetBusinessId) },
          },
          {
            query: GET_REFERRAL_LEADS,
            variables: {
              businessId: Number(targetBusinessId),
              limit: 100,
              offset: 0,
            },
          },
          {
            query: GET_CONCIERGE_LEADS,
            variables: {
              cityCodes: null,
              statuses: null,
              limit: 300,
              offset: 0,
            },
          },
          {
            query: GET_CONCIERGE_BROADCAST_GROUPS,
            variables: {
              limit: 300,
              offset: 0,
            },
          },
          "ReferrerByToken",
          "MyReferrerProfile",
          "MyReferralBroadcastInvitesForPortal",
          "MyReferralBroadcastInvitesCount",
          "MyBroadcastInvites",
        ];
      },
      awaitRefetchQueries: true,
    }
  );

  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);
  const [releaseBusinessClaim, { loading: releasingClaim }] = useMutation(
    RELEASE_BUSINESS_CLAIM
  );

  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);
  const [respondToInvite, { loading: savingInvite }] = useMutation(PROVIDER_RESPOND_INVITE, {
    refetchQueries: (res) => {
      const status = String(res?.data?.providerRespondInvite?.status || "")
        .trim()
        .toUpperCase();
      if (status !== "ACCEPTED" || !targetBusinessId) return [];
      return [
        {
          query: GET_PROVIDER_AGREEMENT_STATUS,
          variables: { businessId: Number(targetBusinessId) },
        },
        "MyBroadcastInvites",
        "MyReferralBroadcastInvitesCount",
      ];
    },
    awaitRefetchQueries: true,
  });

  const emailSignedCopy = async (title: string, agreementBody: string) => {
    const from_email = "no-reply@plany365.com";
    const businessEmails = Array.isArray(businessBasic?.emails)
      ? businessBasic?.emails ?? []
      : [];
    const recipients = normalizeRecipientList([
      userEmail,
      ...businessEmails,
    ]);
    if (!recipients.length) return;

    const acceptedOnText = new Date().toLocaleString();

    const html = buildAgreementEmailHtml({
      title,
      acceptedOnText,
      agreementBody,
      businessName: businessBasic?.name ?? "",
      businessAddress: businessBasic?.address ?? "",
      platformName: "Plany365 Care",
    });

    const text = buildAgreementEmailText({
      title,
      acceptedOnText,
      agreementBody,
      businessName: businessBasic?.name ?? "",
      businessAddress: businessBasic?.address ?? "",
      platformName: "Plany365 Care",
    });

    const subject = `${title} - Accepted by ${businessBasic?.name ?? "Provider"}`;

    await sendEmail({
      variables: {
        from_email,
        to: recipients.join(","),
        subject,
        text,
        html,
      },
    });
  };

  const onAccept = async () => {
    setLocalError(null);
    try {
      if (!userEmail) throw new Error("You must be logged in to accept the agreement.");

      if (inviteId) {
        const res = await respondToInvite({
          variables: {
            inviteId: String(inviteId),
            action: "ACCEPTED",
            agreementAccepted: true,
          },
        });
        const ok =
          String(res?.data?.providerRespondInvite?.status || "").trim().toUpperCase() ===
          "ACCEPTED";
        if (!ok) throw new Error("Request and agreement not accepted.");
      } else if (leadId) {
        const res = await acceptForLead({
          variables: {
            input: {
              businessId: Number(targetBusinessId),
              leadId: String(leadId),
            },
          },
        });
        const ok = res?.data?.acceptProviderAgreementForLead?.success === true;
        if (!ok) throw new Error("Agreement not accepted.");
      } else {
        const res = await acceptGlobal({ variables: { businessId: Number(targetBusinessId) } });
        const ok = res?.data?.acceptProviderAgreement?.success === true;
        if (!ok) throw new Error("Agreement not accepted.");
      }

      if (targetBusinessId > 0) {
        dispatch(setSelectedBusiness(targetBusinessId));
        await updateDefaultBusiness({
          variables: { defaultBusinessId: Number(targetBusinessId) },
        });
      }

      if (merged?.title && merged?.body) {
        await emailSignedCopy(merged.title, merged.body);
      }

      navigate(returnTo || "/biz/dashboard", { replace: true });
    } catch (e: any) {
      setLocalError(e?.message || "Failed to accept agreement.");
    }
  };

  const onReturnToPreviousPage = () => {
    navigate(returnTo || "/biz/dashboard", { replace: true });
  };

  const onDeclineRegistrationClaim = async () => {
    setLocalError(null);
    try {
      if (!targetBusinessId) throw new Error("No business selected.");
      if (!window.confirm("Return this business to unclaimed and stop this registration?")) {
        return;
      }

      const res = await releaseBusinessClaim({
        variables: { businessId: Number(targetBusinessId) },
      });
      const payload = res?.data?.releaseBusinessClaim;
      if (!payload?.success) {
        throw new Error(payload?.message || "Could not return this business to unclaimed.");
      }

      if (payload?.token) {
        setToken(payload.token);
      }

      let previousContext: { defaultBusinessId?: number; selectedBusinessId?: number } | null = null;
      try {
        const raw = sessionStorage.getItem(AGREEMENT_CONTEXT_KEY);
        previousContext = raw ? JSON.parse(raw) : null;
      } catch (_err) {
        previousContext = null;
      }

      const restoreDefaultBusinessId = Number(previousContext?.defaultBusinessId || 0);
      const restoreSelectedBusinessId = Number(
        previousContext?.selectedBusinessId || restoreDefaultBusinessId || 0
      );

      if (restoreDefaultBusinessId > 0) {
        await updateDefaultBusiness({
          variables: { defaultBusinessId: restoreDefaultBusinessId },
        });
      }

      try {
        if (restoreSelectedBusinessId > 0) {
          localStorage.setItem("selectedBusinessId", String(restoreSelectedBusinessId));
        } else {
          localStorage.removeItem("selectedBusinessId");
        }
      } catch (_err) {
        // ignore storage failures
      }

      if (restoreSelectedBusinessId > 0) {
        dispatch(setSelectedBusiness(restoreSelectedBusinessId));
      }

      const restoreTokenSource = payload?.token || getToken();
      const decodedRestoreToken: { userId?: number | null } | null = restoreTokenSource
        ? jwtDecode<{ userId?: number | null }>(restoreTokenSource)
        : null;
      const restoreUserId = Number(providerUser?.userId || decodedRestoreToken?.userId || 0);
      if (restoreUserId > 0 && restoreDefaultBusinessId > 0) {
        const refreshedTokenResult = await apolloClient.query({
          query: GET_PROVIDER_TOKEN,
          variables: {
            userId: restoreUserId,
            businessId: restoreDefaultBusinessId,
            staffId: null,
            subscriptionId: null,
          },
          fetchPolicy: "network-only",
        });
        const refreshedToken = refreshedTokenResult?.data?.getToken?.token;
        if (refreshedToken) {
          setToken(refreshedToken);
          updateProviderUser(jwtDecode(refreshedToken) as any);
        } else if (restoreDefaultBusinessId > 0) {
          updateProviderUser({ defaultBusinessId: restoreDefaultBusinessId });
        }
      } else if (restoreDefaultBusinessId > 0) {
        updateProviderUser({ defaultBusinessId: restoreDefaultBusinessId });
      }

      try {
        sessionStorage.removeItem(AGREEMENT_CONTEXT_KEY);
      } catch (_err) {
        // ignore storage failures
      }

      window.location.assign(restoreSelectedBusinessId > 0 ? "/care" : "/biz/business");
    } catch (e: any) {
      setLocalError(e?.message || "Could not return this business to unclaimed.");
    }
  };

  if (!ready && !targetBusinessId) {
    if (storeLoading) {
      return (
        <Box p={4} textAlign="center">
          <CircularProgress />
          <Typography mt={2}>Loading your business...</Typography>
        </Box>
      );
    }
    return <Alert severity="warning">No business selected.</Alert>;
  }

  if (tplLoading) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
        <Typography mt={2}>Loading agreement...</Typography>
      </Box>
    );
  }

  if (tplError) {
    return <Alert severity="error">{tplError.message}</Alert>;
  }

  if (!merged) return null;

  const btnLabel =
    inviteId || leadId ? "Accept After Review" : "Accept Agreement";

  return (
    <Box maxWidth={1000} mx="auto" p={2}>
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {merged.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Version {merged.version}
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="formatted">Formatted</ToggleButton>
            <ToggleButton value="plain">Plain</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {(leadId || inviteId) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This acceptance applies to{" "}
            <b>{leadId ? `referral #${leadId}` : "this provider request"}</b>. Upon acceptance,
            the referrer will see your contact details for this referral. Medicaid/Medicare/VA
            referrals are free; private-pay fee terms apply.
          </Alert>
        )}

        {!leadId && !inviteId && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            If you do not want to continue with this business agreement, you can return the
            business to unclaimed.
          </Alert>
        )}

        {leadId && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: "rgba(248,250,252,0.8)",
              border: "1px solid rgba(103, 114, 126, 0.14)",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Referral Summary
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>Lead Source:</strong>{" "}
                {agreementLeadSourceType === "AGENT_SOURCED"
                  ? "Agent-Sourced"
                  : "Platform Direct"}
              </Typography>
              <Typography variant="body2">
                <strong>Routing:</strong>{" "}
                {agreementLeadSourceType === "AGENT_SOURCED"
                  ? "Introduced through an agent-associated referral"
                  : "Introduced through the platform and routed by admin/company workflow"}
              </Typography>
              {agreementSourceAgentName ? (
                <Typography variant="body2">
                  <strong>Agent:</strong> {agreementSourceAgentName}
                </Typography>
              ) : null}
              <Typography variant="body2">
                <strong>Fee Trigger:</strong> Move-in / successful placement
              </Typography>
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            p: 2,
            mb: 2,
            width: "100%",
            overflowX: "auto",
            borderRadius: 1,
            bgcolor: "background.paper",
            "& ul": { marginTop: 0, marginBottom: 0 },
          }}
        >
          {viewMode === "formatted" ? (
            <AgreementPrettyViewer body={merged.body} />
          ) : (
            <Typography
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                m: 0,
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {merged.body}
            </Typography>
          )}
        </Box>

        {localError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        )}

        <FormControlLabel
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label="I have read and agree to the terms."
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 1 }}>
          {leadId || inviteId ? (
            <Button
              variant="outlined"
              color="inherit"
              disabled={savingGlobal || savingLead || savingInvite || releasingClaim}
              onClick={onReturnToPreviousPage}
            >
              Back to Current Page
            </Button>
          ) : null}
          <Button
            variant="contained"
            disabled={!checked || savingGlobal || savingLead || savingInvite || releasingClaim}
            onClick={onAccept}
          >
            {savingGlobal || savingLead || savingInvite ? "Saving..." : btnLabel}
          </Button>
          {!leadId && !inviteId ? (
            <Button
              variant="outlined"
              color="inherit"
              disabled={savingGlobal || savingLead || savingInvite || releasingClaim}
              onClick={onDeclineRegistrationClaim}
            >
              {releasingClaim ? "Returning..." : "I don't want this business"}
            </Button>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}

export {};
